import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import type { NavConfig, NavItemConfig } from "@/types/navigation";
import { useNavBadge } from "@/hooks/useNavBadge";
import { cn } from "@/lib/utils";

interface MobileMoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: NavConfig;
  onNavigate: (href: string) => void;
}

function MobileNavButton({
  item,
  onNavigate,
}: {
  item: NavItemConfig;
  onNavigate: () => void;
}) {
  const badge = useNavBadge(item.badge);

  return (
    <button
      onClick={onNavigate}
      className="flex w-full items-center gap-4 rounded-xl border border-border p-3 text-left transition-colors hover:bg-muted/60"
    >
      <span className="flex size-10 items-center justify-center rounded-full bg-muted">
        <item.icon className="size-5 text-muted-foreground" />
      </span>
      <div className="flex-1">
        <p className="text-sm font-semibold">{item.label}</p>
        {item.subLabel && (
          <p className="text-xs text-muted-foreground">{item.subLabel}</p>
        )}
      </div>
      {badge.show && (
        <Badge
          variant={badge.variant === "warning" ? "secondary" : badge.variant}
          className={cn(
            badge.variant === "warning" && "bg-warning/20 text-warning-foreground",
            badge.variant === "destructive" && "bg-destructive/20 text-destructive-foreground"
          )}
        >
          {badge.value}
        </Badge>
      )}
    </button>
  );
}

export function MobileMoreSheet({
  open,
  onOpenChange,
  config,
  onNavigate,
}: MobileMoreSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle>More navigation</SheetTitle>
          <SheetDescription>
            Quick access to workspace tools and resources.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <Button
            className="w-full"
            onClick={() => {
              onNavigate(config.primaryCTA.href);
              onOpenChange(false);
            }}
          >
            <config.primaryCTA.icon className="mr-2 size-4" />
            {config.primaryCTA.label}
          </Button>

          {config.sections.map((section) => (
            <div key={section.id} className="space-y-4">
              {section.label && (
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.label}
                </p>
              )}
              <div className="grid gap-2">
                {section.items.map((item) => (
                  <MobileNavButton
                    key={item.href}
                    item={item}
                    onNavigate={() => {
                      onNavigate(item.href);
                      onOpenChange(false);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
